import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';
import Page from '../../../../../Common/Layout/Page';
import HelpToolTip from '../../../../../Common/HelpToolTip';
import ContractTeamDatatable from '../../Datatables/ContractTeamDatatable';
import ViewAlert from '../../Components/ViewAlert/ViewAlert';
import { withClsCompTracking } from '../../../../../Telemetry/telemetry.service';
import {
	saveUpdatedContractTeams,
	getContractTeams,
	addUpdateToTeam,
	clearUpdateTeamErrors,
	clearAllTeamsUpdates,
	applyMultiTeamUpdate,
	getContractTeamsTemplate,
	removeUpdate,
	removeAllEdits
} from '../../../../../Redux/Actions/Contract.Teams.Actions';
import CloseButton from '../../../../../Common/CloseButton/CloseButton';
import SaveButton from '../../Components/SaveButton/SaveButton';
import classes from '../contract-views.module.css';
import Section from '../../../../../Common/Layout/Section';
import Panel from '../../../../../Common/Layout/Panel';
import TeamTypeFilter from '../../../../../Common/Components/TeamTypeFilter/TeamTypeFilter.container';
import Refiner from '../../../../../Common/Components/Refiner/Refiner.container';
import Updater from '../../../Generic/Appointments/Components/Updater/Updater.container';
import useTeamManagement from "../../Datatables/hooks/useTeamManagement";
import viewTypes from '../../../Generic/Appointments/Components/Updater/ViewType.constants';

const TeamsManagement = withClsCompTracking('Teams Management Table')(ContractTeamDatatable);

const TeamManagement = ({ handleActiveModal, appointeeType = 'Marker' }) => {
	document.title = 'Contract Management - Appointment Management';

	const dispatch = useDispatch();
	const [fireShow, setFireShow] = useState(false);
	const qualification_year = useSelector(state => state.contract_globals.qualification_year.selected);
	const teams = useSelector(state => state.teams);	
	const handleGetContractTeams = (contractTypes, year, query, updated, exportData, teamIds, isRefine, templateId, contractStatus, markingMethod, title, markingTeamCode, isGroupUpdater, previewUpdate, isGroupUpdaterFilter) =>
		dispatch(getContractTeams(contractTypes, year, query, updated, exportData, teamIds, isRefine, templateId, contractStatus, markingMethod, title, markingTeamCode, isGroupUpdater, previewUpdate, isGroupUpdaterFilter));
	const handleTeamUpdate = (field, newValue, row, updates) => dispatch(addUpdateToTeam(field, newValue, row, updates));
	const handleRemoveUpdate = (keyField, id, updates) => dispatch(removeUpdate(keyField, id, updates));
	const handleApplyMultiTeamUpdate = (data, updated, multiTeamData) => dispatch(applyMultiTeamUpdate(data, updated, multiTeamData));
	const handleTeamErrorClear = () => dispatch(clearUpdateTeamErrors());
	const handleClearTeamsUpdates = () => dispatch(clearAllTeamsUpdates());
	const handleRemoveAllEdits = () => dispatch(removeAllEdits());
	const handleGetContractTeamsTemplate = appointeeType => dispatch(getContractTeamsTemplate(appointeeType));
	const saveGroupUpdater = (year, updates) => dispatch(saveUpdatedContractTeams(year, updates));	

	const hasUpdatedField = ({
		displayStatus,
		markingMethodCurrentYear,
		templateId,
		templateName,
		contractReleaseDate,
		contractEndDate,
		emailAddress,
		hidStatus,
		hidMarkingMethod,
		hidTemplateId,
		hidTemplateName,
		hidReleaseDate,
		hidEndDate,
		hidEmailAddress
	}) => {
		const editFields = {
			displayStatus,
			markingMethodCurrentYear,
			templateId,
			templateName,
			contractReleaseDate,
			contractEndDate,
			emailAddress
		};
		const origFields = {
			displayStatus: hidStatus,
			markingMethodCurrentYear: hidMarkingMethod,
			templateId: hidTemplateId,
			templateName: hidTemplateName,
			contractReleaseDate: hidReleaseDate,
			contractEndDate: hidEndDate,
			emailAddress: hidEmailAddress
		};
		return !isEqual(editFields, origFields);
	};

	const amendments = teams.updated.map(hasUpdatedField)
						.filter(item => item)
						.length;

	const selectedCount = teams.updated.map(team => team.selectTeam === 'Y')
							.filter(item => item)
							.length;

	const { filterOpen, toggleFilter, isFiltering, setIsFiltering, year, updateYear, startNewFilter, handleFilterClear, handleFilterSubmit
		, refinerFields, refinerOpen, inPreview, refineData, onRefinementSelectorsChange, onRefinementSelectorsRemove, onRefinementAllClear, toggleRefiner
		, updaterOpen, toggleUpdater, setInPreview, startNewUpdate, updateDataTable, saveData, previewUpdate, isGroupUpdaterFilter, displayData, displayAppointmentsName
		, viewDataOpen, setViewDataOpen, toggleViewData, isYearChange, isDataAPICallCompleted, selectedTypes, selectedTeamIds
		, selectedTemplateId, selectedContractStatus, selectedMarkingMethod, inputTitle, inputMarkingTeamCode, setPageDetails	
	} = useTeamManagement(appointeeType, handleClearTeamsUpdates, handleGetContractTeamsTemplate, handleGetContractTeams, saveGroupUpdater);
	
	const defaultTitle = year ? `${year} ${appointeeType} Appointments` : '';
	const displayTitle = displayAppointmentsName ? `${defaultTitle} ${displayAppointmentsName}` : defaultTitle;
		
	return (
		<Page title="Appointment Management">
			<Section>
				<TeamTypeFilter
					open={filterOpen}
					togglePanel={toggleFilter}
					isFiltering={isFiltering}
					setIsFiltering={setIsFiltering}
					grouping={appointeeType}
                    year={year}
                    numberOfFutureYears={1}
					updateYear={updateYear}
					startNewFilter={startNewFilter}
                    onClear={handleFilterClear}
                    onSubmit={handleFilterSubmit}
				/>
			</Section>
			<Section>
				<Refiner
                    disabled={isFiltering}
                    fields={refinerFields}
					open={refinerOpen}
					readOnly={inPreview}
					data={refineData}
					onRefinementSelectorsChange={onRefinementSelectorsChange}
					onRefinementSelectorsRemove={onRefinementSelectorsRemove}
					onRefinementAllClear={onRefinementAllClear}
					onPanelToggleClick={toggleRefiner}
				/>
			</Section>
			<Section>
				<Updater
                    disabled={isFiltering}
					year={year}
					updaterOpen={updaterOpen}
					setUpdaterOpen={toggleUpdater}
					grouping={appointeeType}
					inPreview={inPreview}
					setInPreview={setInPreview}
					isFiltering={isFiltering}
					refinedAppointments={teams.groupUpdaterData}
					viewDataOpen={viewDataOpen}
					setViewDataOpen={setViewDataOpen}
                    startNewUpdate={startNewUpdate}
					isMarker={true}
					updateDataTable={updateDataTable}
					saveData={saveData}
					displayData={displayData}
					updateViewType={viewTypes.MarkerAppointmentManagement}
				/>
			</Section>
			<Section>
				<Panel variant='default' open={viewDataOpen} disabled={isFiltering} title={displayTitle} onToggle={toggleViewData}>
					{ (viewDataOpen || (year && !isYearChange && isDataAPICallCompleted)) && ( <div>					
						<HelpToolTip title={`Contract Teams Editing Help`} />
						<ViewAlert show={teams.message !== ''} message={teams.message} errors={teams.errors} clearMessage={handleTeamErrorClear} />
						<TeamsManagement
							keyField={'id_type'}
							contractTypes={selectedTypes}
							year={year}
							appointeeType={appointeeType}
							teamIds={selectedTeamIds}	
							templateId={selectedTemplateId}
							contractStatus={selectedContractStatus}	
							markingMethod={selectedMarkingMethod}
							inputTitle={inputTitle}
							inputMarkingTeamCode={inputMarkingTeamCode}
							setPageDetails={setPageDetails}
							previewUpdate={previewUpdate}
							isGroupUpdaterFilter={isGroupUpdaterFilter}
							handleGetData={handleGetContractTeams}
							handleAddUpdates={handleTeamUpdate}
							handleApplyMultiTeamUpdate={handleApplyMultiTeamUpdate}
							removeUpdate={handleRemoveUpdate}
							handleClearUpdates={handleClearTeamsUpdates}
							handleGetTemplate={handleGetContractTeamsTemplate}
							handleRemoveAllEdits={handleRemoveAllEdits}
							fireShow={fireShow}
							amendments={amendments}
							selectedCount={selectedCount}
							setFireShow={setFireShow}
							dispatch={dispatch}
							{...teams}
						/> 
					</div>)}
					<span className={classes['action-buttons']}>
						<CloseButton closeOnClick={() => handleActiveModal('')} hasUpdates={teams.updated && teams.updated.length > 0} />
					</span>	
				</Panel>		
			</Section>
		</Page>
	);
};

export default TeamManagement;
